import React, { useState, useMemo } from "react";
import {
  Box,
  VStack,
  Select,
  Button,
  Input,
  Text,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Checkbox,
  Flex,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
  Center,
  Spinner,
} from "@chakra-ui/react";
import { AddIcon, ChevronDownIcon, DeleteIcon } from "@chakra-ui/icons";
import { 
  ColumnType,
  AggregationType,
  FilterOperator,
  aggregationTypeToJSON,
  filterOperatorToJSON,
  sortByEnumToJSON,
} from "build-ts/enums";
import { ColumnInfo, FilterProto, Header, ObservationColumnProto } from "build-ts/metadata";
import GlobalEntity from "containers/batch/GlobalEntity";
import { DateFilter } from "build-ts/relative_date";
import { DateFilterCategory } from "utils/enum";
import { getDateFilterName } from "utils/nameUtils";

interface ObservationProps {
    type: "observation" | "filter";
    columns: ColumnInfo[];
    columnNames?: string[];
    selectedObservationColumn: Header | null;
    selectedFilterColumn: ColumnInfo | null;
    selectedAggregation?: AggregationType;
    selectedOperator?: FilterOperator;
    inputValue: string;
    selectedColumnValues: string[];
    selectedFilterColumns: FilterProto[];
    selectedObservationColumns: ObservationColumnProto[];
    dateFilter: DateFilter | undefined;
    dateFilterCategory: DateFilterCategory | undefined;
    setDateFilter: React.Dispatch<React.SetStateAction<DateFilter | undefined>>;
    setDateFilterCategory: React.Dispatch<React.SetStateAction<DateFilterCategory>>;
    handleObservationColumnChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
    handleFilterColumnChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
    handleAggregationChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
    handleOperatorChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
    handleValueChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    handleAddObservationColumn: () => void;
    handleAddFilterColumn: () => void;
    handleToggle: (value: string) => void;
    handleDeleteObservationColumn: (index: number) => void;
    handleDeleteFilterColumn: (index: number) => void;
    isLoading: boolean;
}

const Observation: React.FC<ObservationProps> = ({
  type,
  columns,
  columnNames,
  selectedObservationColumn,
  selectedFilterColumn,
  selectedAggregation,
  selectedOperator,
  inputValue,
  selectedColumnValues,
  selectedFilterColumns,
  selectedObservationColumns,
  handleObservationColumnChange,
  handleFilterColumnChange,
  handleAggregationChange,
  handleOperatorChange,
  handleValueChange,
  handleAddObservationColumn,
  handleAddFilterColumn,
  handleToggle,
  handleDeleteObservationColumn,
  handleDeleteFilterColumn,
  dateFilter,
  setDateFilter,
  dateFilterCategory,
  setDateFilterCategory,
  isLoading
}) => {
  
  const globalEntityModal = useDisclosure();
  const [searchTerm, setSearchTerm] = useState("");
  
  // Filter columnNames based on search term
  const filteredColumnNames = useMemo(() => {
    if (!columnNames) return [];
    if (!searchTerm.trim()) return columnNames;
    
    return columnNames.filter(option => {
      const displayValue = option === null ? "{Null}" : option.toString();
      return displayValue.toLowerCase().includes(searchTerm.toLowerCase());
    });
  }, [columnNames, searchTerm]);
  return (
    <Flex direction="column" width="100%">
      <Box p={2} borderRadius="lg" bg="gray.50" width="100%" minH="50px">
        {type === "observation" && selectedObservationColumns.length > 0 && (
          <HStack spacing={5} align="stretch" width="100%">
            <Box w="full">
              {selectedObservationColumns?.map((obs, index) => (
                <Flex
                  key={index}
                  align="center"
                  justify="space-between"
                  px={2}
                  py={1}
                  borderWidth="1px"
                  borderRadius="lg"
                  borderColor="purple.700"
                  bg="bg.surface"
                >
                  <Box>
                    <Text fontSize="xs" fontWeight="600" mb={0}>
                      {aggregationTypeToJSON(obs?.aggregation!).toUpperCase()} (
                      {obs?.logical_column_header?.name}),
                      <Text as="span" fontWeight={400} mb={0}>
                        {" "}
                        Sort By:{" "}
                      </Text>
                      {sortByEnumToJSON(obs?.sort_by!)}
                    </Text>
                  </Box>
                  <Button
                    variant="tertiary"
                    size="xs"
                    leftIcon={<DeleteIcon boxSize="0.8rem" />}
                    onClick={() => handleDeleteObservationColumn(index)}
                    color="red.600"
                  >
                    Clear
                  </Button>
                </Flex>
              ))}
            </Box>
          </HStack>
        )}

        {type === "filter" && selectedFilterColumns.length > 0 && (
          <HStack spacing={5} align="stretch" width="100%">
            <Box w="full">
              {selectedFilterColumns.map((cols, index) => (
                <Flex
                  key={index}
                  align="center"
                  justify="space-between"
                  px={2}
                  py={1}
                  borderWidth="1px"
                  borderRadius="lg"
                  borderColor="purple.700"
                  bg="bg.surface"
                >
                  <HStack spacing="1">
                    <Text
                      fontSize="xs"
                      fontWeight="600"
                      display="inline"
                      mb={0}
                    >
                      {cols?.logical_column_header?.name}
                    </Text>
                    <Text
                      fontSize="xs"
                      fontWeight="600"
                      display="inline"
                      mb={0}
                    >
                      {filterOperatorToJSON(cols.operator!)}
                    </Text>
                    <Text
                      fontSize="xs"
                      fontWeight="600"
                      display="inline"
                      mb={0}
                    >
                      {cols.date_filter ? getDateFilterName(cols.date_filter) 
                      : cols.values && cols.values.join(", ")}
                    </Text>
                  </HStack>
                  <Button
                    variant="tertiary"
                    size="xs"
                    onClick={() => handleDeleteFilterColumn(index)}
                    leftIcon={<DeleteIcon boxSize="0.8em" />}
                    color="red.600"
                  >
                    Clear
                  </Button>
                </Flex>
              ))}
            </Box>
          </HStack>
        )}
      </Box>

      {/* The configuration box */}
      <Box flex="1" p={2}>
        <VStack spacing={3}>
          <Text fontSize="sm" fontWeight="bold" mb={0} w="100%">
            {type === "observation" ? "Aggregation" : "Filters"}
          </Text>

          <Select
            placeholder="Column"
            onChange={type === "observation"? handleObservationColumnChange : handleFilterColumnChange}
            value={type === "observation"? selectedObservationColumn?.id || "": selectedFilterColumn?.header?.id || ""}
            fontSize="sm"
            bg="bg.surface"
          >
            {type === "observation" ? 
              columns?.filter((column) => (column?.column_type === ColumnType.MEASURE))?.map((column) => (
                <option key={column.header?.id} value={column.header?.id}>
                  {column.header?.name}
                </option>
              ))
             :
              columns?.map((column) => (
                <option key={column.header?.id} value={column.header?.id}>
                  {column.header?.name}
                </option>
              ))
            }
          </Select>

          {type === "observation" ? (
            <>
              <Select
                placeholder="Aggregation Function"
                onChange={handleAggregationChange}
                value={selectedAggregation !== undefined ? aggregationTypeToJSON(selectedAggregation): ""}
                fontSize="sm"
                bg="bg.surface"
              >
                <option value="SUM">SUM</option>
                <option value="AVERAGE">AVERAGE</option>
                <option value="COUNT">COUNT</option>
                <option value="UNIQUE_COUNT">UNIQUE_COUNT</option>
                <option value="GROWTH">GROWTH</option>
                <option value="MIN">MIN</option>
                <option value="MAX">MAX</option>
                <option value="NO_AGGREGATION">NO_AGGREGATION</option>
              </Select>
            </>
          ) : (
            <>
              <Select
                placeholder="Operator"
                onChange={handleOperatorChange}
                value={selectedOperator !== undefined? filterOperatorToJSON(selectedOperator): ""}
                fontSize="sm"
                bg="bg.surface"
              >
                <option value="EQ">EQ</option>
                <option value="NE">NE</option>
                <option value="GT">GT</option>
                <option value="GTE">GTE</option>
                <option value="LT">LT</option>
                <option value="LTE">LTE</option>
                <option value="NO_FILTER_OP">NO_FILTER_OP</option>
              </Select>

              {isLoading ? (
                <Center>
                  <Spinner 
                    size="sm"
                    color="purple.500"
                    thickness="2px"
                  />
                </Center>
              ) : (
                columnNames && selectedFilterColumn?.column_type === ColumnType.ATTRIBUTE ? (
                  <Menu closeOnSelect={false}>
                    <MenuButton
                      as={Button}
                      color="fg.default"
                      bg="bg.surface"
                      maxW="100%"
                      width="100%"
                      rightIcon={<ChevronDownIcon mr={-2} />}
                      fontSize="sm"
                      border="1px"
                      borderColor="gray.300"
                      borderRadius="md"
                      fontWeight="normal"
                      _hover={{ bg: "purple.50" }}
                      textAlign="left"
                    >
                      {selectedColumnValues.length > 0
                        ? `Selected (${selectedColumnValues.length})`
                        : "Select Options"}
                    </MenuButton>
                    <MenuList maxHeight="250px" overflowY="auto">
                      {/* Search Input */}
                      <Box p={2} pb={1}>
                        <Input
                          placeholder="Search options..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          size="sm"
                          borderColor="gray.300"
                          _focus={{ borderColor: "purple.400", boxShadow: "0 0 0 1px purple.400" }}
                        />
                      </Box>
                      {/* Options List */}
                      {filteredColumnNames?.map((option, index) => (
                        <MenuItem key={index} minH="40px">
                          <Checkbox
                            isChecked={selectedColumnValues.includes(option)}
                            onChange={() => handleToggle(option)}
                            colorScheme="purple"
                          >
                            {option === null ? "{Null}" : option.toString()}
                          </Checkbox>
                        </MenuItem>
                      ))}
                      {filteredColumnNames.length === 0 && searchTerm.trim() && (
                        <Box p={2} color="gray.500" fontSize="sm">
                          No options found
                        </Box>
                      )}
                    </MenuList>
                  </Menu>
                ) : selectedFilterColumn?.column_type === ColumnType.TEMPORAL ? (
                  <>
                    <Button
                      width="100%"
                      onClick={globalEntityModal.onOpen}
                      color="fg.default"
                      bg="bg.surface"
                      fontSize="sm"
                      border="1px"
                      borderColor="gray.300"
                      borderRadius="md"
                      fontWeight="normal"
                      _hover={{ bg: "purple.50" }}
                    >
                      {inputValue || "Set Date"}
                    </Button>
                    <Modal isOpen={globalEntityModal.isOpen} onClose={globalEntityModal.onClose} size="xl">
                      <ModalOverlay />
                      <ModalContent>
                        <ModalHeader>Select Date Range</ModalHeader>
                        <ModalCloseButton />
                        <ModalBody>
                          <GlobalEntity 
                            setDateFilter={setDateFilter} 
                            dateFilter={dateFilter} 
                            setDateFilterCategory={setDateFilterCategory}
                            dateFilterCategory={dateFilterCategory}
                            id="observation"
                          />
                        </ModalBody>
                        <ModalFooter>
                          <Button colorScheme="purple" mr={3} onClick={globalEntityModal.onClose}>
                            Apply
                          </Button>
                          <Button variant="ghost" onClick={globalEntityModal.onClose}>
                            Cancel
                          </Button>
                        </ModalFooter>
                      </ModalContent>
                    </Modal>
                  </>
                ) : (
                  <Input
                    type="text"
                    placeholder="Values"
                    value={inputValue}
                    onChange={handleValueChange}
                    fontSize="sm"
                  />
                )
              )}
            </>
          )}

          <Button
            colorScheme="purple"
            onClick={type === "observation" ? handleAddObservationColumn : handleAddFilterColumn}
            w="100%"
            leftIcon={<AddIcon boxSize="0.9rem" />}
            fontWeight="bold"
            isDisabled={
              type === "observation" 
                ? selectedObservationColumn === null || selectedAggregation === undefined
                : selectedFilterColumn === null|| selectedOperator === undefined || (
                    selectedFilterColumn?.column_type === ColumnType.ATTRIBUTE 
                      ? selectedColumnValues.length === 0
                      : selectedFilterColumn?.column_type === ColumnType.TEMPORAL
                        ? !dateFilter
                        : !inputValue.trim()
                  )
            }
          >
            Include
          </Button>
        </VStack>
      </Box>
    </Flex>
  );
};

export default Observation;